import type { CustomerCartEvent } from "../customer-cart/CustomerCartEvents";
import type { AnonCartEvent } from "../anon-cart/AnonCartEvents";
import type { IDBPDatabase } from "idb";
import { openDB } from "idb";
import * as AnonCartEvents from "../anon-cart/AnonCartEvents";
import * as CustomerCartEvents from "../customer-cart/CustomerCartEvents";
import { trackUiError } from "../../utils/TrackingUtil";
const BY_CART_ID = "by-cart-id";
const EVENTS_STORE_NAME = "events";
const API_VERSION_2 = 2;
export interface ICartEventRepository<Event> {
	getEvents(cartId: string): Promise<Event[]>;
	saveEvent(cartId: string, event: Event): Promise<void>;
}
export class CartEventRepository<
	Event extends {
		readonly eventId: string;
		readonly seqNum: number;
	},
> implements ICartEventRepository<Event>
{
	database: Promise<IDBPDatabase | void>;
	parse: (json: Record<string, any>) => Event;

	constructor(tableName: string, parse: (json: Record<string, any>) => Event) {
		this.parse = parse;
		this.database = openDB(tableName, API_VERSION_2, {
			upgrade(upgradeDb) {
				if (!upgradeDb.objectStoreNames.contains(EVENTS_STORE_NAME)) {
					const store = upgradeDb.createObjectStore(EVENTS_STORE_NAME);
					store.createIndex(BY_CART_ID, "_cartId", {
						unique: false,
						multiEntry: true,
					});
				}
			},
		}).catch((e) => trackUiError("CartEventRepository", "Could not create IDB database.", e));
	}

	async getEvents(cartId: string): Promise<Event[]> {
		const db = await this.database;

		if (!db) {
			console.warn("Can not read events, because IDB is not available.");
			return [];
		}

		const tx = db.transaction(EVENTS_STORE_NAME, "readonly");
		const objectStore = tx.objectStore(EVENTS_STORE_NAME);

		if (!(objectStore.indexNames as any).contains(BY_CART_ID)) {
			return [];
		}

		const events = await objectStore.index(BY_CART_ID).getAll(cartId);
		return (events || []).map((e) => this.parse(e)).sort((a, b) => a.seqNum - b.seqNum);
	}

	async saveEvent(cartId: string, event: Event): Promise<void> {
		const db = await this.database;

		if (!db) {
			console.warn("Can not store events, because IDB is not available.");
			return Promise.resolve();
		}

		const tx = db.transaction(EVENTS_STORE_NAME, "readwrite");
		tx.objectStore(EVENTS_STORE_NAME)
			.put(
				Object.assign({}, event, {
					_cartId: cartId,
				}),
				event.eventId,
			)
			.catch((e) => trackUiError("CartEventRepository", "Could not save event.", e));
		return tx.done;
	}
}

export const AnonCartEventRepository: ICartEventRepository<AnonCartEvent> = new CartEventRepository(
	"anon-cart-events",
	AnonCartEvents.parseLocalEvent,
);
export const CustomerCartEventRepository: ICartEventRepository<CustomerCartEvent> = new CartEventRepository(
	"customer-cart-events",
	CustomerCartEvents.parseLocalEvent,
);
